<template>
  <div
    v-if="(searchQuery && shouldBeOpen) || !searchQuery"
    class="faq-item"
    :class="
      isExpanded || shouldBeOpen ? 'faq-item--expanded' : 'faq-item--collapsed'
    "
  >
    <div
      class="faq-item__header"
      :class="
        isExpanded || shouldBeOpen
          ? 'faq-item__header--expanded'
          : 'faq-item__header--collapsed'
      "
      @click="isExpanded = !isExpanded"
    >
      <div class="faq-item__title">
        {{ faq.title }}
        <PlusIcon v-if="!(isExpanded || shouldBeOpen)" class="faq-item__icon" />
        <MinusIcon v-else class="faq-item__icon" />
      </div>
      <div
        v-if="descriptionWithHighlightedSearchQuery"
        class="faq-item__header__description"
        v-html="descriptionWithHighlightedSearchQuery"
      ></div>
    </div>
    <transition name="slide">
      <div
        v-if="isExpanded || shouldBeOpen"
        class="faq-item__content faq-item__content--expanded"
      >
        <div
          class="faq-item__content__inner"
          v-html="textHtmlWithHighlightedSearchQuery"
        ></div>
        <div
          v-if="filteredFaqs && filteredFaqs.length"
          class="faq-item__subcontent"
        >
          <FaqItem
            v-for="innerFaq in filteredFaqs"
            :key="innerFaq.title"
            :faq="innerFaq"
            :searchQuery="searchQuery"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { PlusIcon, MinusIcon } from 'vue-feather-icons'
import FaqItem from './FaqItem.vue'

export default {
  name: 'FaqItem',
  components: {
    FaqItem,
    PlusIcon,
    MinusIcon,
  },
  props: {
    faq: {
      type: Object,
      required: true,
    },
    searchQuery: {
      type: String,
      required: false,
      default: '',
    },
  },
  data: () => {
    return {
      isExpanded: false,
    }
  },
  computed: {
    filteredFaqs() {
      const searchQuery = this.searchQuery
      if (!this.searchQuery) {
        return this.faq.content
      } else {
        return this.faq.content?.filter(faq => {
          return (
            faq.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.text_html?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        })
      }
    },
    shouldBeOpen() {
      const searchQuery = this.searchQuery
      if (!searchQuery) {
        return false
      } else {
        return (
          this.faq?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          this.faq?.text_html
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          this.faq.content?.some(
            innerFaq =>
              innerFaq.text_html
                ?.toLowerCase()
                .includes(searchQuery.toLowerCase()) ||
              innerFaq.title?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        )
      }
    },
    descriptionWithHighlightedSearchQuery() {
      const searchQuery = this.searchQuery
      if (!this.searchQuery) {
        return this.faq?.description_html
      } else {
        return this.faq?.description_html?.replace(
          new RegExp(`(?![^<]*>)${searchQuery}(?![^<]*>)`, 'gi'),
          match => `<span class="faq-item__highlight">${match}</span>`
        )
      }
    },
    textHtmlWithHighlightedSearchQuery() {
      const searchQuery = this.searchQuery
      if (!this.searchQuery) {
        return this.faq?.text_html
      } else {
        return this.faq?.text_html?.replace(
          new RegExp(`(?![^<]*>)${searchQuery}(?![^<]*>)`, 'gi'),
          match => `<span class="faq-item__highlight">${match}</span>`
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.faq-item {
  $this: &;
  width: 100%;
  border-radius: 10px;
  background: linear-gradient(
    112deg,
    rgba(187, 188, 199, 0.1) -46.39%,
    rgba(187, 188, 199, 0) 112.38%
  );
  &__header {
    &--collapsed {
      background: linear-gradient(
        112deg,
        rgba(187, 188, 199, 0.14) -46.39%,
        rgba(187, 188, 199, 0) 112.38%
      );
      &:hover {
        cursor: pointer;
        box-shadow: inset 0px 0px 0px 1px var(--tuna-gray);
        background: var(
          --Glass-Mid-gray,
          linear-gradient(
            105deg,
            rgba(62, 63, 73, 0.7) -13.91%,
            rgba(62, 63, 73, 0) 100.48%
          )
        );
      }
      &:active {
        box-shadow: inset 0px 0px 0px 1px var(--tuna-gray);
        background: var(
          --Glass-Dark,
          linear-gradient(
            111deg,
            rgba(18, 18, 18, 0.7) 3.86%,
            rgba(18, 18, 18, 0) 107.96%
          )
        );
      }
    }
    &--expanded {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
      background: var(
        --Glass-Mid-gray,
        linear-gradient(
          105deg,
          rgba(62, 63, 73, 0.7) -13.91%,
          rgba(62, 63, 73, 0) 100.48%
        )
      );
      &:hover {
        cursor: pointer;
        box-shadow: inset 0px 0px 0px 1px var(--tuna-gray);
        background: var(
          --Glass-Light-Grey,
          linear-gradient(
            112deg,
            rgba(187, 188, 199, 0.7) -46.39%,
            rgba(187, 188, 199, 0) 112.38%
          )
        );
      }
      &:active {
        box-shadow: inset 0px 0px 0px 1px var(--tuna-gray);
        background: var(
          --Glass-Dark,
          linear-gradient(
            111deg,
            rgba(18, 18, 18, 0.7) 3.86%,
            rgba(18, 18, 18, 0) 107.96%
          )
        );
      }
    }
    border-radius: 10px;
    color: var(--white);
    font-size: rem(16px);
    font-style: normal;
    font-weight: 700;
    line-height: rem(24px); /* 150% */
    padding: 10px 15px;

    display: flex;
    width: 100%;
    min-height: 44px;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    justify-content: center;
    &__description {
      color: var(--light-blue-gray);
      font-size: rem(14px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px); /* 142.857% */
      padding-right: 44px;
    }
  }
  &__icon {
    margin-left: auto;
    margin-right: 0;
    width: 24px;
    height: 24px;
    color: var(--white);
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }
  &__content {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    transition-duration: 0.1s;
    transition-timing-function: linear;
    background: linear-gradient(
      112deg,
      rgba(187, 188, 199, 0.1) -46.39%,
      rgba(187, 188, 199, 0) 112.38%
    );
    padding: 20px 15px;
    @include mobile {
      padding: 20px 30px;
    }
    &__inner {
      color: var(--light-blue-gray);
      font-size: rem(14px);
      font-style: normal;
      font-weight: 450;
      line-height: rem(20px); /* 142.857% */
      ::v-deep ol {
        margin: 0 23px;
        padding: 0;
      }
      ::v-deep ul {
        margin: 0 23px;
        padding: 0;
      }
      ::v-deep a {
        color: var(--purple-light);
      }
    }
  }
  &__subcontent {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    align-self: stretch;
  }
  ::v-deep &__highlight {
    background: #61539e;
  }

  .slide-enter,
  .slide-leave-to {
    padding-top: 0;
    padding-bottom: 0;
  }
}
</style>
